import React from 'react';
import ReactDOM from 'react-dom';
import { Box, IconButton, Typography } from '@mui/material';
import NavBarIconButtonBadge from '../NavBarIconButtonBadge';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import CloseIcon from '@mui/icons-material/Close';
import { StatusTypes } from 'src/models/types/StatusTypes';
import CardPannel from './FloatingPanelComponent';
import { useTranslation } from 'react-i18next';
import { useErrorDeleter, useErrorList } from "src/context/ErrorProvider";

/**
 * @function FloatingPanel
 * @param {object} listErrors Objetos con la forma:
 * {
 * 		typeMessage: "warning" | "error" | "info" | "success",
 * 		title: string,
 * 		subtitle: string,
 * 		dateTime: Date,
 * 		description: element,
 * 		descriptionCollapse: element
 * }
 * @returns {React.JSX.Element}
 * @example
 * const { error } = StateContext();
 * <FloatingPanel listErrors={error} key="floatPanel"  />
 */
function FloatingPanel(): React.JSX.Element {
  const [expanded, setExpanded] = React.useState(false);
  const { t } = useTranslation();
  const errors = useErrorList();
  const deleteError = useErrorDeleter();

  const hideShow = () => {
    setExpanded(!expanded);
  }

  const portalContainer = document.getElementById('error-portal') || document.body;

  const floatingPanelContent = expanded && (
    <Box
      bgcolor="secondary.light"
      borderRadius="0.4rem"
      sx={{
        width: "30vw",
        position: "fixed",
        top: "30px",
        bottom: "10px",
        right: "10px",
        paddingBottom: "0.5rem",
        maxHeight: "80vw",
        maxWidth: "30vw",
        marginTop: "5rem",
        display: "flex",
        flexDirection: "column",
        opacity: [0.6, 0.5, 0.4],
        zIndex: 1300,
        '&:hover': {
          backgroundColor: "secondary.main",
          opacity: 1,
        }
      }}
    >
      <Box
        bgcolor="secondary.dark"
        sx={{
          padding: "0.5rem",
          verticalAlign: "middle",
          display: "flex",
          WebkitBorderTopLeftRadius: "0.4rem",
          borderTopLeftRadius: "0.4rem",
          WebkitBorderTopRightRadius: "0.4rem",
          borderTopRightRadius: "0.4rem",
        }}
      >
        <Typography
          variant="h4"
          width="100%"
          color="white"
          noWrap
          sx={{
            marginY: "auto",
          }}
        >
          {t("layout.listOfMessages")}
        </Typography>

        <IconButton
          color="secondary"
          aria-label="close"
          onClick={hideShow}
        >
          <CloseIcon sx={{ color: "white" }} />
        </IconButton>
      </Box>
      <Box
        sx={{
          maxWidth: "100%",
          height: "100%",
          verticalAlign: "middle",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "0.5rem"
        }}
      >
        {errors && errors.length > 0 ?
          errors.map(elem => (
            <CardPannel
              key={elem.id} // Asegúrate de que cada error tenga una clave única
              messageType={(elem?.typeMessage as StatusTypes) ?? "success"}
              title={elem?.title ?? t(elem?.typeMessage as StatusTypes) ?? ""}
              subtitle={elem?.subtitle || ""}
              dateTime={elem?.dateTime}
              messageCollapse={elem?.descriptionCollapse || undefined}
              messageCardContent={elem?.description || undefined}
              onDelete={() => deleteError(elem.id!)}
            />
          )) :
          <Typography style={{ textAlign: "center" }}>
            {t("layout.messageIsEmpty")}
          </Typography>
        }
      </Box>
    </Box>
  );

  return (
    <>
      <NavBarIconButtonBadge
        elementIcon={<QuestionAnswerIcon />}
        numBadge={errors?.length ?? 0}
        isDot={true}
        fncAction={hideShow}
      />

      {expanded && ReactDOM.createPortal(floatingPanelContent, portalContainer)}
    </>
  );
}

export default FloatingPanel;
